/**
 * useLC Hook
 *
 * Fetches liquid cultures with related strain data from Supabase.
 * Provides filtering, sorting, and single LC fetching capabilities.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch all liquid cultures with optional filtering
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by status
 * @param {string} options.strainId - Filter by strain
 * @param {number} options.generation - Filter by generation
 * @returns {Object} { liquidCultures, loading, error, refetch }
 */
export function useLiquidCultures(options = {}) {
  const [liquidCultures, setLiquidCultures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchLiquidCultures = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('liquid_cultures')
        .select(`
          *,
          strain:strains(id, code, name, category, origin),
          parent:liquid_cultures!parent_culture_id(id, culture_code)
        `)
        .order('created_at', { ascending: false })

      // Apply filters
      if (options.status) {
        query = query.eq('status', options.status)
      }
      if (options.strainId) {
        query = query.eq('strain_id', options.strainId)
      }
      if (options.generation) {
        query = query.eq('generation', options.generation)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      setLiquidCultures(data || [])
    } catch (err) {
      console.error('[useLiquidCultures] Error:', err)
      setError(err.message)
      setLiquidCultures([])
    } finally {
      setLoading(false)
    }
  }, [options.status, options.strainId, options.generation])

  useEffect(() => {
    fetchLiquidCultures()
  }, [fetchLiquidCultures])

  return {
    liquidCultures,
    loading,
    error,
    refetch: fetchLiquidCultures
  }
}

/**
 * Hook to fetch a single liquid culture by ID with full details
 * @param {string} id - The liquid culture UUID
 * @returns {Object} { liquidCulture, loading, error, refetch }
 */
export function useLiquidCulture(id) {
  const [liquidCulture, setLiquidCulture] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchLiquidCulture = useCallback(async () => {
    if (!id) {
      setLiquidCulture(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('liquid_cultures')
        .select(`
          *,
          strain:strains(id, code, name, category, scientific_name, origin),
          parent:liquid_cultures!parent_culture_id(id, culture_code, generation)
        `)
        .eq('id', id)
        .single()

      if (fetchError) {
        throw fetchError
      }

      setLiquidCulture(data)
    } catch (err) {
      console.error('[useLiquidCulture] Error:', err)
      setError(err.message)
      setLiquidCulture(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchLiquidCulture()
  }, [fetchLiquidCulture])

  return {
    liquidCulture,
    loading,
    error,
    refetch: fetchLiquidCulture
  }
}

/**
 * Hook to fetch all active strains for dropdown selection
 * @returns {Object} { strains, loading, error }
 */
export function useStrains() {
  const [strains, setStrains] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchStrains = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('strains')
          .select('id, code, name, category, scientific_name, origin')
          .eq('active', true)
          .order('name')

        if (fetchError) {
          throw fetchError
        }

        setStrains(data || [])
      } catch (err) {
        console.error('[useStrains] Error:', err)
        setError(err.message)
        setStrains([])
      } finally {
        setLoading(false)
      }
    }

    fetchStrains()
  }, [])

  return { strains, loading, error }
}

/**
 * Hook to fetch all available parent cultures across all strains.
 * Only returns G1 and G2 cultures that are in 'ready' or 'in_use' status.
 * Includes strain information for grouping in UI.
 * @returns {Object} { parentCultures, parentCulturesByStrain, loading }
 */
export function useParentCultures() {
  const [parentCultures, setParentCultures] = useState([])
  const [parentCulturesByStrain, setParentCulturesByStrain] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchParentCultures = async () => {
      setLoading(true)

      try {
        const { data, error: fetchError } = await supabase
          .from('liquid_cultures')
          .select(`
            id,
            culture_code,
            generation,
            status,
            strain_id,
            strain:strains(id, name, code, origin)
          `)
          .in('generation', [1, 2]) // Only G1 and G2 can be parents
          .in('status', ['ready', 'in_use'])
          .order('generation')
          .order('culture_code')

        if (fetchError) {
          throw fetchError
        }

        const cultures = data || []
        setParentCultures(cultures)

        // Group by strain for UI
        const grouped = cultures.reduce((acc, culture) => {
          const strainName = culture.strain?.name || 'Unknown'
          if (!acc[strainName]) {
            acc[strainName] = []
          }
          acc[strainName].push(culture)
          return acc
        }, {})
        setParentCulturesByStrain(grouped)
      } catch (err) {
        console.error('[useParentCultures] Error:', err)
        setParentCultures([])
        setParentCulturesByStrain({})
      } finally {
        setLoading(false)
      }
    }

    fetchParentCultures()
  }, [])

  return { parentCultures, parentCulturesByStrain, loading }
}

export default useLiquidCultures
